/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useState } from 'react'
import { ExternalLink } from 'lucide-react'
import { NumField, ToolHead } from './ToolUi.jsx'
import { RpeGraph, RpeTable } from './RpeChart.jsx'
import WheelSelect from './WheelSelect.jsx'
import { APP_RPE_URL, INCREMENT_OPTIONS, METHODS, REPS, RIR_LABEL, RPE_ROWS, pct, roundToIncrement } from './rpeData.js'

export default function RpeCalculator() {
  const [weight, setWeight] = useState(80)
  const [reps, setReps] = useState(5)
  const [rpe, setRpe] = useState(8)
  const [methodId, setMethodId] = useState('straight')
  const [targetReps, setTargetReps] = useState(8)
  const [targetRpe, setTargetRpe] = useState(8)
  const [increment, setIncrement] = useState(2.5)
  const [calculated, setCalculated] = useState(false)

  const method = METHODS.find((m) => m.id === methodId)
  const straight = methodId === 'straight'
  const rate = pct(rpe, reps)
  const e1rm = weight / (rate / 100)
  const targetPct = pct(targetRpe, targetReps)
  const targetWeight = e1rm * (targetPct / 100)
  const rounded = roundToIncrement(targetWeight, increment)

  return (
    <div className="tl-page">
      <ToolHead title="محاسبه‌گر 1RM و پیشنهاد وزنه" icon="/a/tool-icon-rpe.webp" badge="RPE" caption="تخمین 1RM و وزنه‌ی ست بعدی بر اساس تکرار در ذخیره (RIR)" />
      <div className="tl-grid">
        <section className="tl-col">
          <div className="tl-card tl-card--lilac">
            <div className="tl-field__top">
              <label className="tl-field__label" htmlFor="rpe-method">متد تمرینی</label>
            </div>
            <WheelSelect id="rpe-method" label="متد تمرینی" value={methodId} options={METHODS.map((m) => ({ value: m.id, label: m.fa }))} onChange={setMethodId} />
            {!straight && <p className="tl-note">{method.desc}</p>}
          </div>
          <div className="tl-card tl-card--sky">
            <NumField label="وزنه‌ی جابه‌جا شده" unit="kg" value={weight} min={1} max={400} step={0.5} onChange={setWeight} />
          </div>
          <div className="tl-card tl-card--mint">
            <NumField label="تکرارهای انجام شده" unit="rep" value={reps} min={1} max={12} step={1} onChange={setReps} tickWidth={30} majorEvery={1} midEvery={1} />
          </div>
          <div className="tl-card tl-card--rose">
            <NumField label="RPE در آخرین ست" value={rpe} min={6} max={10} step={0.5} onChange={setRpe} tickWidth={28} majorEvery={1} midEvery={0.5} hint={'RIR ' + RIR_LABEL[rpe]} />
          </div>
          {straight && (
            <div className="tl-card tl-card--yellow">
              <div className="tl-field__top"><span className="tl-field__label">هدف ست بعدی</span></div>
              <div className="tl-selects">
                <label>
                  <span>تکرار هدف</span>
                  <select className="tl-select" value={targetReps} onChange={(e) => setTargetReps(Number(e.target.value))}>
                    {REPS.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </label>
                <label>
                  <span>RPE هدف</span>
                  <select className="tl-select" value={targetRpe} onChange={(e) => setTargetRpe(Number(e.target.value))}>
                    {RPE_ROWS.slice().reverse().map((r) => <option key={r} value={r}>{r} ({RIR_LABEL[r]} RIR)</option>)}
                  </select>
                </label>
                <label>
                  <span>کمترین وزنه‌ی در دسترس (kg)</span>
                  <select className="tl-select" value={increment} onChange={(e) => setIncrement(Number(e.target.value))}>
                    {INCREMENT_OPTIONS.map((v) => <option key={v} value={v}>{v}</option>)}
                  </select>
                </label>
              </div>
            </div>
          )}
          {straight ? (
            <button type="button" className="tl-btn tl-btn--accent tl-btn--lg" onClick={() => setCalculated(true)}>
              محاسبه و دریافت برنامه
            </button>
          ) : (
            <a className="tl-btn tl-btn--accent tl-btn--lg" href={APP_RPE_URL} target="_blank" rel="noopener">
              <span>محاسبه در اپلیکیشن سکویا</span>
              <ExternalLink size={18} aria-hidden="true" />
            </a>
          )}
        </section>
        <section className="tl-col">
          {straight && calculated ? (
            <>
              <div className="tl-card tl-card--paper">
                <div className="tl-stats">
                  <div className="tl-stat tl-stat--yellow">
                    <span>حداکثر یک تکرار تخمینی (e1RM)</span>
                    <strong dir="ltr">{e1rm.toFixed(1)}<small>kg</small></strong>
                    <em dir="ltr">{reps} @ {rpe} ← {rate}%</em>
                  </div>
                  <div className="tl-stat tl-stat--mint">
                    <span>وزنه‌ی پیشنهادی ست بعدی</span>
                    <strong dir="ltr">{rounded.toFixed(2).replace(/\.?0+$/, '')}<small>kg</small></strong>
                    <em dir="ltr">{targetReps} @ {targetRpe} ← {targetPct}%</em>
                  </div>
                </div>
                <div className="tl-bar"><span style={{ width: rate + '%' }} /></div>
              </div>
              <div className="tl-card tl-card--paper">
                <h2 className="tl-h2">نمودار درصد از 1RM</h2>
                <RpeGraph reps={reps} rpe={rpe} target={{ reps: targetReps, rpe: targetRpe, pct: targetPct }} />
              </div>
              <div className="tl-card tl-card--paper">
                <h2 className="tl-h2">جدول RPE (درصد از 1RM)</h2>
                <RpeTable reps={reps} rpe={rpe} target={{ reps: targetReps, rpe: targetRpe }} />
                <div className="tl-legend">
                  <span><i className="is-sel" />ست انجام شده</span>
                  <span><i className="is-target" />ست هدف</span>
                </div>
              </div>
              <div className="tl-card tl-card--paper">
                <h2 className="tl-h2">وزنه‌ی پیشنهادی برای هر تکرار در RPE {targetRpe}</h2>
                <div className="tl-tablewrap">
                  <table className="tl-table" dir="ltr">
                    <thead><tr><th>تکرار</th>{REPS.map((n) => <th key={n}>{n}</th>)}</tr></thead>
                    <tbody>
                      <tr>
                        <th>kg</th>
                        {REPS.map((n) => (
                          <td key={n} className={n === targetReps ? 'is-target' : ''}>
                            {roundToIncrement(e1rm * (pct(targetRpe, n) / 100), increment).toFixed(2).replace(/\.?0+$/, '')}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
                <p className="tl-foot">مبنا: جدول درصد از 1RM بر اساس تکرار و RPE روش RTS در پژوهش Zourdos و همکاران (2016). وزنه‌ها بر اساس کمترین وزنه‌ی در دسترس گرد شده‌ان.</p>
              </div>
            </>
          ) : (
            <div className="tl-card tl-card--paper tl-empty">
              <h2 className="tl-h2">{straight ? 'نمودار RPE' : method.fa}</h2>
              <p>
                {straight
                  ? 'وزنه، تکرار و RPE آخرین ستت رو وارد کن و «محاسبه و دریافت برنامه» رو بزن تا e1RM، وزنه‌ی ست بعدی، نمودار و جدول RPE نمایش داده بشه.'
                  : 'محاسبه‌ی این متد فقط توی اپلیکیشن سکویا انجام می‌شه. با دکمه‌ی «محاسبه در اپلیکیشن سکویا» توی یک تب جدید باز می‌شه.'}
              </p>
              {straight && <RpeGraph reps={reps} rpe={rpe} />}
            </div>
          )}
        </section>
      </div>
    </div>
  )
}
